import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { RecaptchaService } from '../services/recaptcha.service';

const RECAPTCHA_RETRIED = new HttpContextToken<boolean>(() => false);

type ValidationDetail = {
  path?: string;
  msg?: unknown;
};

function recaptchaValidationDetails(response: Pick<HttpErrorResponse, 'error'>): ValidationDetail[] {
  const payload = response.error;
  const details = payload?.error?.details ?? payload?.details;
  return Array.isArray(details) ? details : [];
}

function isSingleUseTokenError(response: unknown): response is HttpErrorResponse {
  const error = response as Partial<HttpErrorResponse> | null;
  if (!error || error.status !== 400) return false;
  return recaptchaValidationDetails(error as Pick<HttpErrorResponse, 'error'>).some((detail) =>
    detail.path === 'recaptcha'
    && String(detail.msg).includes('timeout-or-duplicate')
  );
}

function replaceRecaptchaToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> | null {
  const context = req.context.set(RECAPTCHA_RETRIED, true);
  if (req.headers.has('recaptcha')) {
    return req.clone({
      context,
      headers: req.headers.set('recaptcha', token),
    });
  }
  if (req.body && typeof req.body === 'object' && 'recaptcha' in req.body) {
    return req.clone({
      context,
      body: { ...(req.body as Record<string, unknown>), recaptcha: token },
    });
  }
  return null;
}

/**
 * reCAPTCHA tokens are single-use. If Google reports that a token expired or
 * was already consumed, retry the same protected request once with a fresh token.
 */
export const RecaptchaRetryInterceptor: HttpInterceptorFn = (req, next) => {
  const recaptcha = inject(RecaptchaService);
  return next(req).pipe(
    catchError((response: unknown) => {
      if (req.context.get(RECAPTCHA_RETRIED) || !isSingleUseTokenError(response)) {
        return throwError(() => response);
      }
      return from(recaptcha.fetch()).pipe(
        switchMap((token) => {
          const retry = replaceRecaptchaToken(req, token);
          return retry ? next(retry) : throwError(() => response);
        })
      );
    })
  );
};
